/**
 * Vocabulário das regras do 8-Ball.
 *
 * Baseado nas World Standardized Rules da WPA, com um conjunto de opções para
 * as variações de bar — que é o que a maioria das pessoas conhece por "regra
 * de sinuca" e o que faz sentido para uma partida casual.
 *
 * Separado da física de propósito: a engine sabe onde as bolas pararam, e é
 * este módulo que diz o que isso SIGNIFICA.
 */

export const SOLIDS = [1, 2, 3, 4, 5, 6, 7] as const
export const STRIPES = [9, 10, 11, 12, 13, 14, 15] as const
export const EIGHT_BALL = 8
export const CUE_BALL = 0

export type Group = 'solids' | 'stripes'

/** Antes da definição de grupos, a mesa está aberta para os dois. */
export type GroupAssignment = { open: true } | { open: false; first: Group; second: Group }

export type PlayerIndex = 0 | 1

export type Pocket = 0 | 1 | 2 | 3 | 4 | 5

// -------------------------------------------------------------- variantes

/**
 * Ajustes de regra.
 *
 * A WPA exige declarar bola e caçapa em TODA tacada. Numa partida casual isso
 * é um atrito enorme na interface, e a maioria das mesas de bar não cobra.
 * Por isso é configurável — mas o padrão do projeto está declarado abaixo e a
 * escolha é explícita, não acidental.
 */
export type RuleSet = {
  /**
   * Quando o jogador precisa declarar bola e caçapa.
   *
   * `pocket` é a regra do projeto desde a v8 do replay: em TODA tacada depois
   * da quebra o jogador declara só a CAÇAPA, e a tacada vale se alguma bola
   * dele cair nela — as outras que caírem junto valem também. Nenhuma bola
   * dele na caçapa declarada encerra a vez, sem falta (como a WPA faz com a
   * declaração não cumprida). Na 8, a 8 tem de cair na caçapa declarada.
   */
  callShot: 'always' | 'eight-only' | 'never' | 'pocket'
  /**
   * Quebra válida: encaçapar alguma bola OU levar este número de bolas à
   * tabela. Abaixo disso, o adversário escolhe o que fazer.
   */
  breakMinBallsToRail: number
  /**
   * Falta na quebra restringe a bola na mão à "cozinha" (atrás da linha da
   * cabeça). Fora da quebra, bola na mão é sempre em qualquer lugar.
   */
  kitchenAfterBreakScratch: boolean
  /**
   * Encaçapar a 8 na quebra derrota?
   *
   * Pela WPA, NÃO: o quebrador escolhe entre recolocar a 8 e seguir, ou
   * quebrar de novo. Muitas mesas de bar tratam como derrota.
   */
  eightOnBreakLoses: boolean
  /**
   * Três faltas seguidas do mesmo jogador entregam a partida.
   *
   * LIGADA no padrão do projeto, e não é preferência de estilo: sem ela não
   * existe nenhum mecanismo que encerre uma partida em que os dois só cometem
   * faltas. Numa partida a dinheiro, "os dois preferem o reembolso" é um acordo
   * plausível, e a única saída restante era estourar o teto de tacadas do
   * replay — que anula a partida e devolve o dinheiro. A regra existe para a
   * partida TERMINAR.
   *
   * Diferença assumida em relação à WPA: ela exige AVISAR o adversário antes da
   * terceira falta valer, e aqui não há aviso. O aviso é um ato verbal de mesa
   * de bar sem equivalente na rede, e a contagem de faltas está o tempo todo na
   * tela dos dois — a informação que o aviso carrega já está dada.
   */
  threeFoulRule: boolean
}

/** Padrão do projeto: WPA com declaração só na 8. */
export const DEFAULT_RULES: RuleSet = {
  callShot: 'eight-only',
  breakMinBallsToRail: 4,
  kitchenAfterBreakScratch: true,
  eightOnBreakLoses: false,
  threeFoulRule: true,
}

/**
 * As regras do projeto desde a v8 do replay: as do padrão, com a caçapa
 * declarada em toda tacada (`callShot: 'pocket'`).
 *
 * Existe ao lado de `DEFAULT_RULES`, e não no lugar dele, porque as partidas
 * gravadas antes continuam sendo reproduzidas com a declaração só na 8 — é a
 * `EpocaDasRegras` de cada uma que escolhe entre as duas.
 */
export const POCKET_RULES: RuleSet = {
  ...DEFAULT_RULES,
  callShot: 'pocket',
}

/** Fiel à WPA. */
export const WPA_RULES: RuleSet = {
  callShot: 'always',
  breakMinBallsToRail: 4,
  kitchenAfterBreakScratch: true,
  eightOnBreakLoses: false,
  threeFoulRule: true,
}

/** Regra de bar: sem declaração, quebra sempre vale, 8 na quebra perde. */
export const BAR_RULES: RuleSet = {
  callShot: 'never',
  breakMinBallsToRail: 0,
  kitchenAfterBreakScratch: false,
  eightOnBreakLoses: true,
  threeFoulRule: false,
}

// ----------------------------------------------------------------- faltas

export type FoulReason =
  | 'cue-ball-pocketed'
  | 'cue-ball-off-table'
  | 'no-contact'
  | 'wrong-ball-first'
  | 'eight-ball-first-illegal'
  | 'no-rail-after-contact'
  | 'ball-off-table'
  | 'no-call'

/**
 * Todo motivo de falta, como LISTA.
 *
 * O tipo acima é o que o julgamento produz; esta lista é o que a página de
 * regras percorre para explicar cada um. As duas precisam andar juntas, e o
 * `satisfies` mais a conferência de exaustividade logo abaixo garantem isso
 * pelo compilador: um motivo novo no tipo que não entrar aqui não compila —
 * e a página de regras, que tem um teste por código, é o que faria uma regra
 * sem explicação quebrar o build.
 */
export const FOUL_REASONS = [
  'cue-ball-pocketed',
  'cue-ball-off-table',
  'no-contact',
  'wrong-ball-first',
  'eight-ball-first-illegal',
  'no-rail-after-contact',
  'ball-off-table',
  'no-call',
] as const satisfies readonly FoulReason[]

type FoulReasonSemLista = Exclude<FoulReason, (typeof FOUL_REASONS)[number]>
const _todaFaltaListada: [FoulReasonSemLista] extends [never] ? true : FoulReasonSemLista = true
void _todaFaltaListada

/** Onde a bola na mão pode ser colocada. */
export type BallInHand =
  | { active: false }
  /** Em qualquer ponto livre da mesa. */
  | { active: true; region: 'anywhere' }
  /** Atrás da linha da cabeça, após falta na quebra. */
  | { active: true; region: 'kitchen' }

/**
 * Decisão pendente entre tacadas.
 *
 * A WPA dá escolhas ao adversário em duas situações, e nenhuma pode ser
 * resolvida automaticamente sem tirar do jogador um direito que ele tem.
 */
export type PendingChoice =
  | { kind: 'eight-on-break'; chooser: PlayerIndex }
  | { kind: 'illegal-break'; chooser: PlayerIndex }

/** As situações de decisão, como lista — para a página de regras explicar cada uma. */
export const PENDING_CHOICE_KINDS = [
  'eight-on-break',
  'illegal-break',
] as const satisfies readonly PendingChoice['kind'][]

type DecisaoSemLista = Exclude<PendingChoice['kind'], (typeof PENDING_CHOICE_KINDS)[number]>
const _todaDecisaoListada: [DecisaoSemLista] extends [never] ? true : DecisaoSemLista = true
void _todaDecisaoListada

export type BreakChoice = 'accept' | 'rerack-self' | 'rerack-opponent' | 'respot-eight'

export type MatchState = {
  turn: PlayerIndex
  groups: GroupAssignment
  /** Ids já encaçapados e fora da mesa. */
  pocketed: number[]
  broken: boolean
  /** Quem quebrou este rack. Volta a quebrar em caso de empate técnico. */
  breaker: PlayerIndex
  ballInHand: BallInHand
  /** Faltas seguidas por jogador, para a regra das três faltas. */
  consecutiveFouls: [number, number]
  /** Escolha aguardando o jogador, se houver. */
  pending: PendingChoice | null
  winner: PlayerIndex | null
  ending: MatchEnding | null
  /**
   * Esta partida declara a caçapa em toda tacada (a época dela, ver
   * `EpocaDasRegras`). Viaja no estado, e não só na criação, porque é o
   * estado que as regras recebem a cada tacada — e um rack refeito no meio da
   * partida precisa continuar na mesma época.
   */
  cacapaEmTodaTacada?: boolean
}

export type MatchEnding =
  | { kind: 'eight-ball-potted' }
  | { kind: 'eight-ball-early' }
  | { kind: 'eight-ball-wrong-pocket' }
  | { kind: 'eight-ball-with-foul'; foul: FoulReason }
  | { kind: 'eight-ball-off-table' }
  | { kind: 'three-fouls' }
  | { kind: 'forfeit' }

/**
 * Todo jeito de a partida acabar, como LISTA — pelo mesmo motivo de
 * `FOUL_REASONS`: a página de regras explica cada um, e um fim novo sem
 * explicação tem de quebrar o build, não aparecer cru na tela de derrota.
 */
export const MATCH_ENDING_KINDS = [
  'eight-ball-potted',
  'eight-ball-early',
  'eight-ball-wrong-pocket',
  'eight-ball-with-foul',
  'eight-ball-off-table',
  'three-fouls',
  'forfeit',
] as const satisfies readonly MatchEnding['kind'][]

type FimSemLista = Exclude<MatchEnding['kind'], (typeof MATCH_ENDING_KINDS)[number]>
const _todoFimListado: [FimSemLista] extends [never] ? true : FimSemLista = true
void _todoFimListado

/**
 * O que a física relatou sobre uma tacada.
 *
 * É a ponte entre os dois módulos: a física produz isto, as regras consomem.
 */
export type ShotOutcome = {
  /** Primeira bola que a branca tocou, ou null. */
  firstContact: number | null
  /** Encaçapadas nesta tacada, na ordem. */
  pocketed: number[]
  /** Bolas que saíram da mesa (puladas). */
  offTable: number[]
  /**
   * Quantas bolas NUMERADAS tocaram alguma tabela. Usado só na quebra, para
   * decidir se ela foi válida.
   */
  ballsToRail: number
  /** Alguma bola (inclusive a branca) tocou a tabela após o primeiro contato. */
  railAfterContact: boolean
  /** Caçapa onde a 8 caiu, se caiu. */
  eightBallPocket: Pocket | null
  /**
   * Em que caçapa cada bola encaçapada caiu, pelo id. Opcional para quem monta
   * o outcome à mão (testes antigos); sem ele, nenhuma bola "caiu na caçapa
   * declarada" pela regra `pocket`.
   */
  pockets?: Readonly<Record<number, Pocket>>
  /** Declaração do jogador antes da tacada. */
  called: { ball: number; pocket: Pocket } | null
}

export type ShotRuling = {
  foul: FoulReason | null
  continues: boolean
  /**
   * A vez terminou sem falta — o jogador declarou uma bola e ela não caiu.
   *
   * Precisa ser um campo próprio, e não um `FoulReason`, porque todo o resto do
   * pipeline trata `FoulReason` como falta: dava bola na mão ao adversário,
   * contava para a regra das três faltas e, em cima da 8, virava derrota. Pela
   * WPA 8.1 a tacada só não conta: as bolas caídas ficam caídas e o adversário
   * joga a mesa como está.
   */
  inningEnds: boolean
  groups: GroupAssignment
  winner: PlayerIndex | null
  ending: MatchEnding | null
  /** Escolha que o adversário passa a ter. */
  pending: PendingChoice | null
  /** A 8 precisa voltar para a mesa (encaçapada numa quebra legal). */
  respotEight: boolean
}

// ------------------------------------------------------------- utilidades

export const otherPlayer = (p: PlayerIndex): PlayerIndex => (p === 0 ? 1 : 0)

export const groupOf = (ball: number): Group | null => {
  if (ball >= 1 && ball <= 7) return 'solids'
  if (ball >= 9 && ball <= 15) return 'stripes'
  return null
}

export function groupFor(groups: GroupAssignment, player: PlayerIndex): Group | null {
  if (groups.open) return null
  return player === 0 ? groups.first : groups.second
}

/** Ids que faltam para o jogador limpar o grupo. */
export function remainingFor(state: MatchState, player: PlayerIndex): number[] {
  const grupo = groupFor(state.groups, player)
  if (!grupo) return []

  const doGrupo = grupo === 'solids' ? SOLIDS : STRIPES
  return doGrupo.filter((id) => !state.pocketed.includes(id))
}

/** O jogador já pode mirar na 8? */
export const canShootEight = (state: MatchState, player: PlayerIndex): boolean =>
  !state.groups.open && remainingFor(state, player).length === 0

/**
 * O que muda entre ÉPOCAS das regras.
 *
 * Um replay gravado antes de uma mudança precisa ser reproduzido com as regras
 * de quando foi jogado — senão "auditável para sempre" vale só até a próxima
 * mudança, e uma partida liquidada na chain passaria a ter outro vencedor.
 * Ver `READABLE_VERSIONS`, em `@sol-pool/replay`.
 */
export type EpocaDasRegras = {
  /**
   * A quebra começa com a branca na mão, na cozinha.
   *
   * Verdadeiro a partir da v6. Antes disso o jogo quebrava sempre do ponto de
   * saque, e é assim que os replays v5 e anteriores precisam ser reproduzidos.
   */
  quebraComBolaNaMao: boolean
  /**
   * Toda tacada depois da quebra declara a caçapa (`POCKET_RULES`).
   *
   * Verdadeiro a partir da v8. Antes disso só a 8 exigia declaração, e é assim
   * que os replays v7 e anteriores precisam ser reproduzidos. Opcional para
   * quem montava a época antes deste campo existir: ausente vale falso, que é
   * a regra de quando esse código foi escrito.
   */
  cacapaEmTodaTacada?: boolean
}

/** A época CORRENTE: o que uma partida nova usa. */
export const EPOCA_ATUAL: EpocaDasRegras = { quebraComBolaNaMao: true, cacapaEmTodaTacada: true }

export function createMatch(
  breaker: PlayerIndex = 0,
  epoca: EpocaDasRegras = EPOCA_ATUAL,
): MatchState {
  return {
    turn: breaker,
    groups: { open: true },
    pocketed: [],
    broken: false,
    breaker,
    /*
     * A QUEBRA NASCE COM A BRANCA NA MÃO, na cozinha — desde a v6.
     *
     * Pela WPA, quem quebra coloca a branca em qualquer ponto atrás da linha
     * da cabeça. Faltava, e quem joga pediu: a quebra é a tacada que mais
     * depende do ponto de saída, e obrigar todo mundo a sair do mesmo lugar
     * empobrece justamente a tacada em que os dois lados têm a mesma chance.
     *
     * É DIREITO, não etapa: quem não arrastar a branca quebra do ponto de
     * saque, como sempre. Por isso as partidas gravadas antes desta regra
     * continuam se reproduzindo — elas simplesmente não têm evento `place`
     * antes da primeira tacada, e a branca fica onde já estava.
     */
    ballInHand: epoca.quebraComBolaNaMao
      ? { active: true, region: 'kitchen' }
      : { active: false },
    consecutiveFouls: [0, 0],
    pending: null,
    winner: null,
    ending: null,
    ...(epoca.cacapaEmTodaTacada ? { cacapaEmTodaTacada: true } : {}),
  }
}
